import { useMemo, useRef, useState, useEffect } from 'react';
import { useMemoizedFn } from 'ahooks';
import * as echarts from 'echarts/core';
import useChartResize from '@gp/hooks/echarts/useChartResize';
import useChartRefreshWhenOptionsChange from '@gp/hooks/echarts/useChartRefreshWhenOptionsChange';
import DataTypeUtils from '@gp/utils/DataTypeUtils';
import type { ILocale } from '@gp/types/global';

type IEChartLocale = 'ZH' | 'EN';

const LOCALES_MAP: {
  [key: string]: IEChartLocale;
} = {
  zh: 'ZH',
  en: 'EN',
};

const DEFAULT_ECHART_LOCALE = 'EN';

/**
 * 初始化echarts实例，组件卸载时，销毁echart实例
 */
export default function useChart(config: {
  // 初始化配置项（一般指静态echarts配置项，不依赖异步获取的数据的配置项）
  initialOption?: IEChartOption;
  // locale语言环境
  locale?: ILocale;
}) {
  const { initialOption, locale } = config || {};

  // chart node节点
  const chartNode = useRef<HTMLDivElement>(null);
  // chart 实例
  const chartInstance = useRef<IEChartInstanceRefObject>({
    instance: null,
  });

  // echart 多语言 语言环境
  const chartLocale = useMemo<IEChartLocale>(() => {
    // @ts-ignore
    return LOCALES_MAP[locale] || DEFAULT_ECHART_LOCALE;
  }, [locale]);

  // echart 配置
  const [option, setOption] = useState<IEChartOption>(initialOption || {});

  // 监听window的resize事件，处理echarts resize
  useChartResize(chartInstance);

  // echart配置项变化时,更新echart UI
  useChartRefreshWhenOptionsChange(chartInstance, option);

  // 获取echart实例
  const getChartInstance = useMemoizedFn(() => {
    return chartInstance?.current?.instance;
  });

  // 初始化chart实例
  useEffect(() => {
    if (!chartNode?.current) return;
    if (!chartInstance?.current) return;
    if (chartInstance?.current?.instance) return;

    chartInstance.current.instance = echarts.init(
      chartNode.current,
      undefined,
      {
        // 设备像素比，默认取浏览器的值window.devicePixelRatio
        devicePixelRatio: window?.devicePixelRatio,
        // 渲染模式，支持'canvas'或者'svg'
        renderer: 'canvas',
        // 是否使用服务端渲染，只有在 SVG 渲染模式有效。开启后不再会每帧自动渲染，必须要调用 renderToSVGString 方法才能得到渲染后 SVG 字符串。
        ssr: false,
        // 是否开启脏矩形渲染，只有在 Canvas 渲染模式有效，默认为false
        useDirtyRect: false,
        // 可显式指定实例宽度，单位为像素。如果传入值为null/undefined/'auto'，则表示自动取 dom（实例容器）的宽度
        width: undefined,
        // 可显式指定实例高度，单位为像素。如果传入值为null/undefined/'auto'，则表示自动取 dom（实例容器）的高度
        height: undefined,
        // 使用的语言，内置 'ZH' 和 'EN' 两个语言
        locale: chartLocale,
      },
    );

    /**
     * 组件卸载时，销毁echart实例
     */
    return () => {
      if (
        chartInstance?.current &&
        DataTypeUtils.isFunction(chartInstance?.current?.instance?.dispose)
      ) {
        chartInstance?.current?.instance?.dispose();
        chartInstance.current.instance = null;
      }
    };
  }, [chartLocale]);

  return {
    chartNode,
    option,
    setOption,
    getChartInstance,
  };
}
